import { Component } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw, Home, Wifi, WifiOff, FileWarning } from "lucide-react";

function ErrorFallback({ error, errorInfo, onReset, onGoHome, t }) {
  const errorType = (() => {
    if (!error) return "render";
    const msg = (error.message || "").toLowerCase();
    if (msg.includes("network") || msg.includes("fetch") || msg.includes("failed to load")) return "network";
    if (msg.includes("404") || msg.includes("not found")) return "notfound";
    return "render";
  })();

  const config = {
    network: {
      icon: WifiOff,
      iconColor: "text-amber-400",
      borderColor: "border-amber-500/30",
      bgColor: "bg-amber-500/10",
      titleKey: "error.network_title",
      defaultTitle: "Connection Error",
      descKey: "error.network_desc",
      defaultDesc: "A network error occurred. Check your connection and try again.",
    },
    notfound: {
      icon: FileWarning,
      iconColor: "text-zinc-400",
      borderColor: "border-zinc-500/30",
      bgColor: "bg-zinc-500/10",
      titleKey: "error.not_found_title",
      defaultTitle: "Page Not Found",
      descKey: "error.not_found_desc",
      defaultDesc: "The page you're looking for doesn't exist or has been moved.",
    },
    render: {
      icon: AlertTriangle,
      iconColor: "text-red-400",
      borderColor: "border-red-500/30",
      bgColor: "bg-red-500/10",
      titleKey: "error.render_title",
      defaultTitle: "Something went wrong",
      descKey: "error.render_desc",
      defaultDesc: "An unexpected error occurred. Please try again.",
    },
  }[errorType];

  const Icon = config.icon;
  const title = t(config.titleKey, { defaultValue: config.defaultTitle });
  const desc = t(config.descKey, { defaultValue: config.defaultDesc });

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className={`w-14 h-14 mx-auto mb-5 border ${config.borderColor} ${config.bgColor} rounded flex items-center justify-center`}>
          <Icon className={`w-7 h-7 ${config.iconColor}`} />
        </div>
        <h2 className="font-display text-lg sm:text-xl text-white mb-2 uppercase tracking-wide">
          {title}
        </h2>
        <p className="text-zinc-500 text-sm mb-2 font-mono">
          {desc}
        </p>
        {error?.message && (
          <p className="text-zinc-600 text-xs mb-6 font-mono break-all">
            {error.message}
          </p>
        )}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            onClick={onReset}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-700 hover:border-amber-500 text-zinc-300 hover:text-amber-500 font-mono text-xs uppercase tracking-wider transition-colors min-h-[44px] min-w-[44px]"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {t("error.try_again", { defaultValue: "Try Again" })}
          </button>
          <button
            onClick={onGoHome}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-700 hover:border-amber-500 text-zinc-300 hover:text-amber-500 font-mono text-xs uppercase tracking-wider transition-colors min-h-[44px] min-w-[44px]"
          >
            <Home className="w-3.5 h-3.5" />
            {t("error.go_home", { defaultValue: "Go Home" })}
          </button>
        </div>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (fallback) return fallback;

      return (
        <ErrorBoundaryWrapper
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorBoundaryWrapper({ error, errorInfo, onReset, onGoHome }) {
  const { t } = useTranslation();
  return (
    <ErrorFallback
      error={error}
      errorInfo={errorInfo}
      onReset={onReset}
      onGoHome={onGoHome}
      t={t}
    />
  );
}

export default ErrorBoundary;
